import { useEffect, useState } from "react";
import axios from "axios";

const cache = new Map();

// Same-origin proxy on the Laravel backend forwards to
// https://daowhatsapp.duckdns.org server-side. Avoids the CORS block
// when the browser tried to hit duckdns directly in production.
const DAOWHATSAPP_BASE = "/api/integrations/daowhatsapp";

function fetchTradeMatch(listingCode) {
  if (cache.has(listingCode)) return cache.get(listingCode);
  const url = `${DAOWHATSAPP_BASE}/approved_matches?confidence_threshold=0.5&watch_count=all&limit=100&intent=Sell&message_id=${encodeURIComponent(listingCode)}`;
  // Shared axios instance, not a raw fetch() — carries the Authorization and
  // X-Active-Store headers (axios.defaults.headers.common), which a bare
  // fetch() never picks up. This proxy scopes by the caller's tenant same as
  // everything else, so a raw fetch silently resolved under the wrong store.
  //
  // `silent`: this is a decorative thumbnail, one request per trade row. When
  // the WhatsApp proxy is down every row fails at once and each failure was
  // reported to Monitoring separately (#1361: 14 UNREACHABLE events in 6s for
  // one page). The row already shows "No photo" on failure, so keep it off the
  // board and out of the toast path.
  const promise = axios.get(url, { metadata: { silent: true } })
    .then((r) => r?.data ?? null)
    .then((data) => {
      // The WhatsApp message behind the price: photo, text, sender, when.
      const matches = data?.matches || [];
      return matches.find((m) => m?.whatsapp_image_url) || matches[0] || null;
    })
    .catch(() => {
      // Don't pin a failure for the life of the tab: the next mount of this
      // row (reopening the tab) may try again. Successes stay cached.
      cache.delete(listingCode);
      return null;
    });
  cache.set(listingCode, promise);
  return promise;
}

/**
 * The WhatsApp message a trade price came from (looked up by its listing code):
 * { whatsapp_image_url, message_text, message_date, sender_name, sender_phone,
 * group_name, … } or null. `resolved` turns true once the lookup has answered.
 */
export function useTradeMatch(item) {
  const isTrade = item?.source === "trade" && !!item?.listing_code;
  const [match, setMatch] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!isTrade) {
      setMatch(null);
      setResolved(false);
      return;
    }
    let active = true;
    setResolved(false);
    fetchTradeMatch(item.listing_code).then((found) => {
      if (!active) return;
      setMatch(found);
      setResolved(true);
    });
    return () => { active = false; };
  }, [isTrade, item?.listing_code]);

  return { isTrade, match, resolved };
}

export default function useTradeImage(item) {
  const { isTrade, match, resolved } = useTradeMatch(item);
  const src = match?.whatsapp_image_url || null;

  return {
    src,
    showPlaceholder: isTrade && resolved && !src,
  };
}
